import { useState, useEffect, useCallback } from "react";
import { BrandAuthContext } from "./context";
import { brandService } from "../../services/brand.service";
import { toast } from "react-hot-toast";
import { BrandUser } from "../../types/brand";

export function BrandAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<BrandUser | null>(() => {
    // Initialize user from localStorage or sessionStorage if available
    const savedUser =
      localStorage.getItem("brand_user") ||
      sessionStorage.getItem("brand_user");
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(() => {
    const token =
      localStorage.getItem("brand_token") ||
      sessionStorage.getItem("brand_token");
    const brandTempToken =
      localStorage.getItem("brand_temp_token") ||
      sessionStorage.getItem("brand_temp_token");
    // Use brand_token if available, otherwise fallback to brand_temp_token, check both storages
    return token || brandTempToken;
  });
  const [version, setVersion] = useState(0); // Add version state for forcing re-renders

  const initializeAuth = useCallback(async () => {
    console.log("initializeAuth called");
    const attemptAuth = async (): Promise<boolean> => {
      try {
        console.log("Attempting to fetch brand details...");
        // If we have a token, try to fetch brand details
        const apiResponse = await brandService.getBrandDetails();
        const brandDetails = apiResponse.data;
        if (apiResponse.status === 403) {
          return false;
        } else if (apiResponse.status === 401) {
          toast.error("Session expired. Please login again.");
          return false;
        }
        console.log("brandDetails received:", brandDetails);

        if (brandDetails) {
          const user = {
            id: brandDetails.id,
            name: `${brandDetails.firstName} ${brandDetails.lastName}`,
            email: brandDetails.email,
            shop:
              typeof brandDetails.shop === "string" ? brandDetails.shop : "",
            // The user's personal avatar — NOT brandProfileImage, which is
            // the company logo and now renders as the portal's brand mark.
            profileImage:
              brandDetails.profileImage &&
              !brandDetails.profileImage.includes("ui-avatars.com")
                ? brandDetails.profileImage
                : null,
            role: "BRAND" as const,
            companyName: brandDetails.companyName,
          };
          console.log("Constructed user object:", user);
          setUser(user);
          localStorage.setItem("brand_user", JSON.stringify(user));
          window.dispatchEvent(new CustomEvent("brand_user_updated"));
          setVersion((v) => v + 1); // Increment version on successful auth
          console.log(
            "User state updated successfully, calling setUser with:",
            user
          );
          return true;
        } else {
          console.log("brandDetails is falsy:", brandDetails);
          return false;
        }
      } catch (error) {
        console.log("Error in attemptAuth:", error);
        return false;
      }
    };

    // First attempt
    const success = await attemptAuth();
    if (success) return;

    // Both attempts failed, remove token and show error
    localStorage.removeItem("brand_token");
    localStorage.removeItem("brand_user");
    window.dispatchEvent(new CustomEvent("brand_user_updated"));
    setAuthToken(null);
  }, []);

  // Watch for token changes in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem("brand_token");
      setAuthToken(newToken);
      setVersion((v) => v + 1); // Increment version on token change
    };

    // Listen for cross-tab storage events
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom event for same-tab token updates
    const handleTokenUpdate = () => {
      const newToken = localStorage.getItem("brand_token");
      // Always update when event is dispatched (caller ensures token is set)
      // The useEffect watching authToken will handle initialization
      setAuthToken(newToken);
      setVersion((v) => v + 1);
    };

    window.addEventListener("brand_token_updated", handleTokenUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("brand_token_updated", handleTokenUpdate);
    };
  }, []);

  // Initialize auth when token changes
  useEffect(() => {
    const checkAuth = async () => {
      console.log("checkAuth called with authToken:", authToken);
      setIsLoading(true);
      try {
        if (authToken) {
          console.log("authToken exists, calling initializeAuth");
          await initializeAuth();
        } else {
          console.log("No authToken, clearing user state");
          setUser(null);
          localStorage.removeItem("brand_user");
        }
      } catch (error) {
        console.log("Error in checkAuth:", error);
        localStorage.removeItem("brand_user");
      } finally {
        setIsLoading(false);
        console.log("checkAuth completed, setting isLoading to false");
      }
    };

    checkAuth();
  }, [authToken, initializeAuth]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    version, // Include version in context value
  };

  console.log(
    "BrandAuthProvider render - user:",
    user,
    "isAuthenticated:",
    !!user,
    "isLoading:",
    isLoading
  );

  return (
    <BrandAuthContext.Provider value={value}>
      {children}
    </BrandAuthContext.Provider>
  );
}
